"use client";

import React, { useEffect, useState } from "react";
import { apiClient } from "../../../lib/apiClient";
import { PreguntaPAES } from "../../../services/admin/simulaciones/simulaciones.service";

interface PreguntaDetailModalProps {
  pregunta: PreguntaPAES | null;
  isOpen: boolean;
  onClose: () => void;
  mode: "view" | "edit" | "create";
  onSave?: (pregunta: PreguntaPAES) => void;
}

const PreguntaDetailModal: React.FC<PreguntaDetailModalProps> = ({
  pregunta,
  isOpen,
  onClose,
  mode,
  onSave,
}) => {
  const [editData, setEditData] = useState<PreguntaPAES | null>(pregunta);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [asignaturas, setAsignaturas] = useState<
    Array<{
      idAsignatura: number;
      nombre: string;
      descripcion: string;
      icono: string;
    }>
  >([]);

  // Normaliza texto de alternativas para que sea JSON parseable
  const normalizeAlternativasString = (raw: string): string => {
    if (!raw) return raw;
    let normalized = raw;
    // Quitar escapes no válidos de JSON como "\$" (pero NO tocar comas dentro de strings)
    normalized = normalized.replace(/\\\$/g, "$");
    // Asegurar comillas dobles estándar (por si vienen comillas tipográficas)
    normalized = normalized.replace(/[“”]/g, '"');
    return normalized;
  };

  const tryParseAlternativas = (raw: string): any => {
    // Intento directo
    try {
      return JSON.parse(raw);
    } catch {}
    // Intento con normalización
    const normalized = normalizeAlternativasString(raw);
    return JSON.parse(normalized);
  };

  // Cargar asignaturas al montar el componente
  useEffect(() => {
    const cargarAsignaturas = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        if (!token) {
          console.error("No hay token de autenticación");
          return;
        }

        const response = await apiClient.get("/simulaciones/asignaturas");
        setAsignaturas(response.data.asignaturas || []);
      } catch (error) {
        console.error("Error al cargar asignaturas:", error);
        // Fallback: asignaturas por defecto
        setAsignaturas([
          {
            idAsignatura: 1,
            nombre: "Matemáticas",
            descripcion: "Matemáticas",
            icono: "",
          },
        ]);
      }
    };

    cargarAsignaturas();
  }, []);

  useEffect(() => {
    if (pregunta) {
      setEditData(pregunta);
    } else {
      setEditData(null);
    }
    setSelectedImage(null);
    setValidationErrors([]);
  }, [pregunta, isOpen]);

  const handleSave = async () => {
    if (!editData || !onSave) return;

    // Validar formulario antes de guardar
    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    setValidationErrors([]); // Limpiar errores previos

    try {
      // Crear objeto solo con los campos que cambiaron
      let dataToSave: any = {};

      if (mode === "edit" && pregunta) {
        // Para edición: comparar con datos originales
        Object.keys(editData).forEach((key) => {
          const originalValue = (pregunta as any)[key];
          const newValue = (editData as any)[key];

          // Solo incluir si el valor cambió
          if (JSON.stringify(originalValue) !== JSON.stringify(newValue)) {
            dataToSave[key] = newValue;
          }
        });

        // Siempre incluir el ID de la pregunta original para actualizaciones
        dataToSave.idPreguntaPAES = safePregunta.idPreguntaPAES;
      } else {
        // Para creación: incluir todos los campos
        dataToSave = { ...editData };
      }

      // Siempre incluir la imagen si se seleccionó una nueva
      if (selectedImage) {
        dataToSave.imagen = selectedImage;
      }

      // Si alternativas es string, convertir a objeto normalizado antes de enviar
      if (typeof dataToSave.alternativas === "string") {
        try {
          dataToSave.alternativas = tryParseAlternativas(
            dataToSave.alternativas
          );
        } catch {}
      }

      await onSave(dataToSave);
      setSelectedImage(null); // Limpiar imagen seleccionada después de guardar
      onClose();
    } catch (error) {
      console.error("Error al guardar:", error);
      setValidationErrors([
        "Error al guardar la pregunta. Inténtalo de nuevo.",
      ]);
    } finally {
      setIsSaving(false);
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
    }
  };

  const validateForm = (): boolean => {
    const errors: string[] = [];

    if (!editData) {
      errors.push("Datos del formulario no válidos");
      setValidationErrors(errors);
      return false;
    }

    if (!editData.contenido || editData.contenido.trim() === "") {
      errors.push("El contenido de la pregunta es requerido");
    }

    if (!editData.respuesta || editData.respuesta.trim() === "") {
      errors.push("La respuesta correcta es requerida");
    }

    if (!editData.explicacion || editData.explicacion.trim() === "") {
      errors.push("La explicación es requerida");
    }

    if (
      !editData.alternativas ||
      (typeof editData.alternativas === "string" &&
        editData.alternativas.trim() === "")
    ) {
      errors.push("Las alternativas son requeridas");
    } else {
      // Validar que las alternativas sean JSON válido
      try {
        if (typeof editData.alternativas === "string") {
          // Solo validar (no reescribir el contenido del usuario)
          void tryParseAlternativas(editData.alternativas);
        }
      } catch (error) {
        errors.push("Las alternativas deben tener un formato JSON válido");
      }
    }

    if (!editData.asignaturaId || editData.asignaturaId === 0) {
      errors.push("La asignatura es requerida");
    }

    // Solo validar campos adicionales en modo create
    if (mode === "create") {
      if (!editData.anio || editData.anio === 0) {
        errors.push("El año es requerido");
      }

      if (!editData.periodo || editData.periodo.trim() === "") {
        errors.push("El período es requerido");
      }

      if (!editData.numero || editData.numero === 0) {
        errors.push("El número de pregunta es requerido");
      }

      if (!editData.tipo || editData.tipo.trim() === "") {
        errors.push("El tipo de pregunta es requerido");
      }

      if (!editData.ejeTematico || editData.ejeTematico.trim() === "") {
        errors.push("El eje temático es requerido");
      }

      if (!editData.fuente || editData.fuente.trim() === "") {
        errors.push("La fuente es requerida");
      }
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  if (!isOpen) return null;

  // Para modo create, no necesitamos pregunta inicial
  if (mode === "create" && !editData) {
    // Crear datos iniciales vacíos para crear nueva pregunta
    const initialData: PreguntaPAES = {
      idPreguntaPAES: 0,
      contenido: "",
      respuesta: "",
      explicacion: "",
      anio: new Date().getFullYear(),
      periodo: "Regular", // Valor por defecto
      numero: 1,
      tipo: "PAES", // Valor por defecto
      asignaturaId: 1, // Asignatura por defecto
      dificultad: 3,
      tiempoEstimado: 60,
      tags: [],
      ejeTematico: "",
      numeroIntentos: 0,
      vecesRespondida: 0,
      porcentajeCorrectas: 0,
      revisada: false,
      creadoPor: "",
    };
    setEditData(initialData);
    return null; // Retornar null para que se re-renderice con los datos iniciales
  }

  // Verificar que tengamos los datos necesarios
  if (!editData) return null;

  // Para modos que no son create, también necesitamos pregunta
  if (mode !== "create" && !pregunta) return null;

  // Crear una referencia segura a pregunta para evitar errores de null
  const safePregunta = pregunta || editData;

  const getDificultadColor = (dificultad: number) => {
    if (dificultad <= 2) return "text-green-600 bg-green-100";
    if (dificultad <= 3) return "text-yellow-600 bg-yellow-100";
    return "text-red-600 bg-red-100";
  };

  const getDificultadText = (dificultad: number) => {
    if (dificultad <= 2) return "Fácil";
    if (dificultad <= 3) return "Media";
    return "Difícil";
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {mode === "view"
              ? "Detalles de la Pregunta"
              : mode === "edit"
              ? "Editar Pregunta"
              : "Nueva Pregunta"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Errores de validación */}
        {validationErrors.length > 0 && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mx-6 mt-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Campos requeridos faltantes:
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <ul className="list-disc pl-5 space-y-1">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {/* Campos ocultos requeridos por el backend */}
          {(mode === "create" || mode === "edit") && (
            <>
              <input type="hidden" value={editData.anio} />
              <input type="hidden" value={editData.periodo} />
              <input type="hidden" value={editData.tipo} />
            </>
          )}

          {/* Campos pequeños arriba en grid - Primera fila */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4 mb-4">
            {/* Mostrar ID solo en modo view o edit (no en create) */}
            {mode !== "create" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ID
                </label>
                <div className="text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-2 rounded-md border border-blue-200">
                  #{safePregunta.idPreguntaPAES}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Número
              </label>
              {mode === "edit" || mode === "create" ? (
                <input
                  type="number"
                  value={editData.numero}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      numero: parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-full text-sm text-gray-900 bg-white border border-gray-300 px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                  {safePregunta.numero}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Asignatura
              </label>
              {mode === "edit" || mode === "create" ? (
                <select
                  value={editData.asignaturaId}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      asignaturaId: parseInt(e.target.value) || 1,
                    })
                  }
                  className="w-full text-sm text-gray-900 bg-white border border-gray-300 px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {asignaturas.map((asignatura) => (
                    <option
                      key={asignatura.idAsignatura}
                      value={asignatura.idAsignatura}
                    >
                      {asignatura.nombre}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                  {asignaturas.find(
                    (a) => a.idAsignatura === safePregunta.asignaturaId
                  )?.nombre || `Asignatura ${safePregunta.asignaturaId}`}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Respuesta
              </label>
              {mode === "edit" || mode === "create" ? (
                <select
                  value={editData.respuesta}
                  onChange={(e) =>
                    setEditData({ ...editData, respuesta: e.target.value })
                  }
                  className="w-full text-sm text-gray-900 bg-white border border-gray-300 px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                  <option value="D">D</option>
                </select>
              ) : (
                <div className="text-sm font-bold text-green-800 bg-green-100 px-3 py-2 rounded-md">
                  {safePregunta.respuesta}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dificultad
              </label>
              {mode === "edit" || mode === "create" ? (
                <select
                  value={editData.dificultad}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      dificultad: parseInt(e.target.value) || 3,
                    })
                  }
                  className="w-full text-sm text-gray-900 bg-white border border-gray-300 px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={1}>1 - Muy Fácil</option>
                  <option value={2}>2 - Fácil</option>
                  <option value={3}>3 - Media</option>
                  <option value={4}>4 - Difícil</option>
                  <option value={5}>5 - Muy Difícil</option>
                </select>
              ) : (
                <div className="text-sm">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDificultadColor(
                      safePregunta.dificultad
                    )}`}
                  >
                    {getDificultadText(safePregunta.dificultad)} (
                    {safePregunta.dificultad}/5)
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Segunda fila de campos pequeños */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Creado por
              </label>
              {mode === "edit" || mode === "create" ? (
                <input
                  type="text"
                  value={editData.creadoPor || ""}
                  onChange={(e) =>
                    setEditData({ ...editData, creadoPor: e.target.value })
                  }
                  className="w-full text-sm text-gray-900 bg-white border border-gray-300 px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                  {safePregunta.creadoPor || "N/A"}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Eje Temático
              </label>
              {mode === "edit" || mode === "create" ? (
                <select
                  value={editData.ejeTematico || ""}
                  onChange={(e) =>
                    setEditData({ ...editData, ejeTematico: e.target.value })
                  }
                  className="w-full text-sm text-gray-900 bg-white border border-gray-300 px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecciona eje temático</option>
                  <option value="NUMEROS">Números</option>
                  <option value="ALGEBRA_FUNCIONES">Álgebra y Funciones</option>
                  <option value="GEOMETRIA">Geometría</option>
                  <option value="PROBABILIDAD_ESTADISTICA">
                    Probabilidad y Estadística
                  </option>
                </select>
              ) : (
                <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                  {safePregunta.ejeTematico || "N/A"}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fuente
              </label>
              {mode === "edit" || mode === "create" ? (
                <input
                  type="text"
                  value={editData.fuente || ""}
                  onChange={(e) =>
                    setEditData({ ...editData, fuente: e.target.value })
                  }
                  className="w-full text-sm text-gray-900 bg-white border border-gray-300 px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                  {safePregunta.fuente || "N/A"}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags
              </label>
              {mode === "edit" || mode === "create" ? (
                <input
                  type="text"
                  value={editData.tags.join(", ")}
                  onChange={(e) => {
                    const tags = e.target.value
                      .split(",")
                      .map((tag) => tag.trim())
                      .filter((tag) => tag);
                    setEditData({ ...editData, tags });
                  }}
                  placeholder="tag1, tag2, tag3"
                  className="w-full text-sm text-gray-900 bg-white border border-gray-300 px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              ) : (
                <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                  {safePregunta.tags.length > 0
                    ? safePregunta.tags.join(", ")
                    : "Sin tags"}
                </div>
              )}
            </div>
          </div>

          {/* Tercera fila de campos pequeños */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Revisada
              </label>
              {mode === "edit" || mode === "create" ? (
                <select
                  value={editData.revisada ? "true" : "false"}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      revisada: e.target.value === "true",
                    })
                  }
                  className="w-full text-sm text-gray-900 bg-white border border-gray-300 px-3 py-2 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="false">No</option>
                  <option value="true">Sí</option>
                </select>
              ) : (
                <div className="text-sm">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      safePregunta.revisada
                        ? "text-green-800 bg-green-100"
                        : "text-red-800 bg-red-100"
                    }`}
                  >
                    {safePregunta.revisada ? "Sí" : "No"}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Campos grandes abajo ocupando todo el ancho */}
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contenido de la Pregunta
              </label>
              {mode === "edit" || mode === "create" ? (
                <textarea
                  value={editData.contenido}
                  onChange={(e) =>
                    setEditData({ ...editData, contenido: e.target.value })
                  }
                  className="w-full text-sm text-gray-900 bg-white border border-gray-300 px-3 py-2 rounded-md min-h-[200px] focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                  rows={12}
                  placeholder="Escribe aquí el contenido de la safePregunta..."
                />
              ) : (
                <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md min-h-[200px] whitespace-pre-wrap">
                  {safePregunta.contenido}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Explicación
              </label>
              {mode === "edit" || mode === "create" ? (
                <textarea
                  value={editData.explicacion}
                  onChange={(e) =>
                    setEditData({ ...editData, explicacion: e.target.value })
                  }
                  className="w-full text-sm text-gray-900 bg-white border border-gray-300 px-3 py-2 rounded-md min-h-[200px] focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                  rows={12}
                  placeholder="Escribe aquí la explicación de la respuesta..."
                />
              ) : (
                <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md min-h-[200px] whitespace-pre-wrap">
                  {safePregunta.explicacion}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Alternativas (JSON)
              </label>
              {mode === "edit" || mode === "create" ? (
                <textarea
                  value={
                    typeof editData.alternativas === "string"
                      ? editData.alternativas
                      : JSON.stringify(editData.alternativas || {}, null, 2)
                  }
                  onChange={(e) => {
                    // Permitir cualquier texto durante la edición
                    setEditData({ ...editData, alternativas: e.target.value });
                  }}
                  className="w-full text-sm text-gray-900 bg-white border border-gray-300 px-3 py-2 rounded-md min-h-[120px] focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y font-mono"
                  rows={6}
                  placeholder='{"A": "Opción A", "B": "Opción B", "C": "Opción C", "D": "Opción D"}'
                />
              ) : (
                <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md min-h-[120px] font-mono whitespace-pre-wrap">
                  {safePregunta.alternativas
                    ? JSON.stringify(safePregunta.alternativas, null, 2)
                    : "Sin alternativas"}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Imagen
              </label>
              {mode === "edit" || mode === "create" ? (
                <div className="space-y-3">
                  {/* Mostrar imagen actual si existe */}
                  {(safePregunta.imagenUrl || editData?.imagenUrl) &&
                    !selectedImage && (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-600">Imagen actual:</p>
                        <div className="relative inline-block">
                          <img
                            src={editData?.imagenUrl || safePregunta.imagenUrl}
                            alt="Imagen actual"
                            className="max-w-full h-auto max-h-32 rounded-md border"
                          />
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log("editData antes:", editData);
                              setEditData({
                                ...editData,
                                imagenUrl: undefined,
                              });
                              console.log("editData después:", {
                                ...editData,
                                imagenUrl: undefined,
                              });
                            }}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors z-10"
                            title="Eliminar imagen"
                            type="button"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}

                  {/* Botón de seleccionar archivo personalizado */}
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="flex items-center justify-center w-full px-4 py-3 text-sm font-medium text-gray-700 bg-white border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors cursor-pointer"
                    >
                      <svg
                        className="w-5 h-5 mr-2 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      {selectedImage
                        ? "Cambiar imagen"
                        : safePregunta.imagenUrl
                        ? "Reemplazar imagen"
                        : "Seleccionar imagen"}
                    </label>
                  </div>

                  {selectedImage && (
                    <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <svg
                        className="w-5 h-5 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-800">
                          {selectedImage.name}
                        </p>
                        <p className="text-xs text-green-600">
                          {(selectedImage.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <button
                        onClick={() => setSelectedImage(null)}
                        className="text-green-600 hover:text-green-800 transition-colors"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md">
                  {safePregunta.imagenUrl ? (
                    <img
                      src={safePregunta.imagenUrl}
                      alt="Imagen de la pregunta"
                      className="max-w-full h-auto max-h-64 rounded-md"
                    />
                  ) : (
                    "Sin imagen"
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Datos Gráficos (JSON)
              </label>
              {mode === "edit" || mode === "create" ? (
                <>
                  <textarea
                    value={
                      editData.datosGrafico
                        ? JSON.stringify(editData.datosGrafico, null, 2)
                        : ""
                    }
                    onChange={(e) => {
                      try {
                        const datosGrafico = e.target.value
                          ? JSON.parse(e.target.value)
                          : null;
                        setEditData({ ...editData, datosGrafico });
                      } catch (error) {
                        // Mantener el texto aunque no sea JSON válido por ahora
                      }
                    }}
                    className="w-full text-sm text-gray-900 bg-white border border-gray-300 px-3 py-2 rounded-md min-h-[120px] focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y font-mono"
                    rows={6}
                    placeholder='{"kind": "geoscene", "version": 1, "viewport": {"xmin": -2, "xmax": 10, "ymin": -3, "ymax": 8}}'
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Ingresa los datos JSON para gráficos interactivos (opcional)
                  </p>
                </>
              ) : (
                <div className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-md min-h-[120px] font-mono whitespace-pre-wrap">
                  {safePregunta.datosGrafico
                    ? JSON.stringify(safePregunta.datosGrafico, null, 2)
                    : "Sin datos gráficos"}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        {mode === "edit" || mode === "create" ? (
          <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? "Guardando..." : "Guardar"}
            </button>
          </div>
        ) : (
          <div className="flex justify-end p-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Cerrar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreguntaDetailModal;
